import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Loader2 } from 'lucide-react'
import { getLocalizedPath, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

type ArticleData = {
  title: string
  content: string
  url: string
}

type NewsModalProps = {
  slug: string | null
  isOpen: boolean
  onClose: () => void
  knownSlugs?: Set<string>
  onOpenSlug?: (slug: string) => void
}

// Map old-site paths (without leading/trailing slashes) to our route keys.
const OLD_SITE_PATH_MAP: Record<string, RouteKey> = {
  '': 'home',
  'prodotti': 'products',
  'prodotti/capelli': 'productsHair',
  'prodotti/novita': 'productsNovita',
  'prodotti/corpo': 'productsCorpo',
  'prodotti/viso': 'productsViso',
  'prodotti/fragranze': 'productsFragranze',
  'prodotti/uomo': 'productsUomo',
  'contattaci': 'contacts',
  'il-mondo-nashi-argan': 'world',
  'faq': 'faq',
  'privacy-policy': 'privacy',
  'cookie-policy': 'cookiePolicy',
  'nashilovers-club': 'nashiLoversClub',
  'news': 'news',
  'diventa-partner': 'diventaPartner',
  'nashi-spring-collection-2026': 'springCollection',
  'store-locator': 'storeLocator',
  'storelocator': 'storeLocator',
  'saloni': 'salon',
  'sitemap': 'sitemap',
  'condizioni-generali-di-vendita-welcome-box': 'condizioniWelcomeBox',
  'regali-aziendali': 'regaliAziendali',
  'regolamento': 'regolamento',
  'e-gift-card': 'eGiftCard',
  'lavora-con-noi': 'lavoraConNoi',
  'nashi-store': 'nashiStore',
  'nashi-partner': 'nashiPartner',
  'welcome-box': 'welcomeBox',
  'login': 'login',
  'product-category/viso': 'productsViso',
  'product-category/capelli': 'productsHair',
  'product-category/corpo': 'productsCorpo',
  'product-category/fragranze': 'productsFragranze',
  'product-category/uomo': 'productsUomo',
  'product-category/novita': 'productsNovita',
}

// Token placeholders so HTML escape doesn't mangle our generated tags.
const IMG_TOKEN = ' IMG '
const LINKED_IMG_TOKEN = ' LIMG '
const LINK_TOKEN = ' LINK '

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function sanitizeUrl(raw: string): string | null {
  const url = raw.trim()
  if (!url) return null
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(url)) return url
  return null
}

function isUsefulImage(src: string): boolean {
  if (!src) return false
  if (/^blob:/i.test(src)) return false
  if (/\.svg(\?|$)/i.test(src)) return false // theme icons (chevron/spacer arrows)
  return /^https?:\/\//i.test(src)
}

function applyInlineEmphasis(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/(^|\s)_([^_\n]+)_(?=$|[\s.,;:!?])/g, '$1<em>$2</em>')
}

function formatNewsMarkdown(content: string, articleTitle?: string): string {
  // 1. Extract image/link tokens BEFORE HTML escaping so the URLs survive.
  const linkedImages: Array<{ alt: string; src: string; href: string }> = []
  const images: Array<{ alt: string; src: string }> = []
  const links: Array<{ text: string; href: string }> = []

  let working = content

  // Linked image: [![alt](src)](href)
  working = working.replace(/\[!\[([^\]]*)\]\(([^)]+)\)\]\(([^)]+)\)/g, (_m, alt, src, href) => {
    const cleanHref = sanitizeUrl(href)
    if (!isUsefulImage(src) || !cleanHref) return ''
    const idx = linkedImages.length
    linkedImages.push({ alt: alt || '', src, href: cleanHref })
    return `${LINKED_IMG_TOKEN}${idx}${LINKED_IMG_TOKEN}`
  })

  // Image: ![alt](src)
  working = working.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, src) => {
    if (!isUsefulImage(src)) return ''
    const idx = images.length
    images.push({ alt: alt || '', src })
    return `${IMG_TOKEN}${idx}${IMG_TOKEN}`
  })

  // Link: [text](href)
  working = working.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, href) => {
    const cleanHref = sanitizeUrl(href)
    if (!cleanHref) return text
    const idx = links.length
    links.push({ text, href: cleanHref })
    return `${LINK_TOKEN}${idx}${LINK_TOKEN}`
  })

  // 2. HTML-escape the rest of the text.
  let html = escapeHtml(working)

  // 3. Inline emphasis. Run bold first so it doesn't get eaten by italics.
  html = applyInlineEmphasis(html)

  // 4. Headings (skip the H1 if it matches the article title we already render in the header).
  const titleNorm = (articleTitle || '').trim().toLowerCase()
  html = html.replace(/^####\s+(.+)$/gm, '<h4 class="font-display text-bruno text-lg mt-6 mb-3">$1</h4>')
  html = html.replace(/^###\s+(.+)$/gm, '<h3 class="font-display text-bruno text-xl mt-8 mb-4">$1</h3>')
  html = html.replace(/^##\s+(.+)$/gm, '<h2 class="font-display text-bruno text-2xl mt-10 mb-4">$1</h2>')
  html = html.replace(/^#\s+(.+)$/gm, (_m, t: string) => {
    if (titleNorm && t.trim().toLowerCase() === titleNorm) return ''
    return `<h2 class="font-display text-bruno text-2xl md:text-3xl mt-10 mb-5">${t}</h2>`
  })

  // 5. Bullet lists: group consecutive "* item" / "- item" lines into <ul>.
  html = html.replace(/(?:^[ \t]*[*-][ \t]+.+(?:\n|$))+/gm, (block) => {
    const items = block
      .trim()
      .split(/\n/)
      .map((line) => line.replace(/^[ \t]*[*-][ \t]+/, '').trim())
      .filter(Boolean)
      .map((it) => `<li class="text-bruno-soft leading-relaxed">${it}</li>`)
      .join('')
    return `<ul class="list-disc pl-6 space-y-2 my-5">${items}</ul>`
  })

  // 6. Paragraphs (blank-line separated). Don't wrap blocks that already start with a tag we generated.
  html = html
    .split(/\n{2,}/)
    .map((para) => {
      const trimmed = para.trim()
      if (!trimmed) return ''
      if (/^<(h\d|ul|ol|li|figure|blockquote)/.test(trimmed)) return trimmed
      return `<p class="text-bruno-soft leading-relaxed mb-5">${trimmed.replace(/\n/g, '<br />')}</p>`
    })
    .filter(Boolean)
    .join('\n')

  // 7. Replace tokens with the actual generated HTML.
  html = html.replace(new RegExp(`${LINKED_IMG_TOKEN}(\\d+)${LINKED_IMG_TOKEN}`, 'g'), (_m, n) => {
    const it = linkedImages[Number(n)]
    if (!it) return ''
    return `<a href="${escapeHtml(it.href)}" target="_blank" rel="noopener noreferrer" class="block my-6"><img src="${escapeHtml(it.src)}" alt="${escapeHtml(it.alt)}" loading="lazy" class="w-full h-auto" /></a>`
  })
  html = html.replace(new RegExp(`${IMG_TOKEN}(\\d+)${IMG_TOKEN}`, 'g'), (_m, n) => {
    const it = images[Number(n)]
    if (!it) return ''
    return `<img src="${escapeHtml(it.src)}" alt="${escapeHtml(it.alt)}" loading="lazy" class="w-full h-auto my-6" />`
  })
  html = html.replace(new RegExp(`${LINK_TOKEN}(\\d+)${LINK_TOKEN}`, 'g'), (_m, n) => {
    const it = links[Number(n)]
    if (!it) return ''
    const renderedText = applyInlineEmphasis(escapeHtml(it.text))
    return `<a href="${escapeHtml(it.href)}" target="_blank" rel="noopener noreferrer" class="underline decoration-argan/40 underline-offset-2 hover:text-argan-deep transition-colors">${renderedText}</a>`
  })

  return html
}

export function NewsModal({ slug, isOpen, onClose, knownSlugs, onOpenSlug }: NewsModalProps) {
  const [article, setArticle] = useState<ArticleData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const articleRef = useRef<HTMLElement | null>(null)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (!isOpen || !slug) {
      setArticle(null)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetch(`/api/news-article?slug=${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || 'Errore nel caricamento')
        }
        return res.json()
      })
      .then((data: ArticleData) => {
        if (!cancelled) {
          setArticle(data)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Errore nel caricamento dell\'articolo')
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, slug])

  // Scroll to top of modal body when slug changes
  useEffect(() => {
    if (slug) {
      bodyRef.current?.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [slug])

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  // Intercept clicks on old-site links and rewrite them to internal SPA navigation.
  const handleArticleClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const anchor = target.closest('a') as HTMLAnchorElement | null
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href) return

      try {
        const url = new URL(href, window.location.href)
        if (url.hostname !== 'nashiargan.com' && url.hostname !== 'www.nashiargan.com') {
          return
        }

        const path = url.pathname.replace(/^\/|\/$/g, '')

        const routeKey = OLD_SITE_PATH_MAP[path]
        if (routeKey) {
          e.preventDefault()
          const newPath = getLocalizedPath(routeKey, i18n.language as SupportedLanguage)
          navigate(newPath)
          return
        }

        // Fallback for product detail pages: /prodotti/<slug>/ or /products/<slug>/
        if (path.startsWith('prodotti/')) {
          const productSlug = path.slice('prodotti/'.length)
          e.preventDefault()
          navigate(`/${i18n.language}/prodotti/${productSlug}`)
          return
        }

        if (path.startsWith('products/')) {
          const productSlug = path.slice('products/'.length)
          e.preventDefault()
          navigate(`/${i18n.language}/prodotti/${productSlug}`)
          return
        }

        // Known news slugs: open inside the same modal stack
        if (knownSlugs?.has(path)) {
          e.preventDefault()
          onOpenSlug?.(path)
          return
        }
      } catch {
        // Ignore malformed URLs
      }
    },
    [i18n.language, navigate, knownSlugs, onOpenSlug]
  )

  useEffect(() => {
    const el = articleRef.current
    if (!el) return
    el.addEventListener('click', handleArticleClick)
    return () => el.removeEventListener('click', handleArticleClick)
  }, [handleArticleClick])

  const formatContent = (content: string) => formatNewsMarkdown(content, article?.title)

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-bruno/60 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl max-h-[85vh] bg-cream border border-bruno/10 shadow-2xl shadow-bruno/20 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-5 md:px-10 md:py-8 border-b border-bruno/10 shrink-0">
              <div className="pr-8">
                <span className="nashi-eyebrow">Nashi Magazine</span>
                {article?.title && (
                  <h2 className="mt-3 font-display text-bruno text-xl md:text-2xl leading-tight">
                    {article.title}
                  </h2>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 mt-1 p-2 border border-bruno/15 text-bruno-muted hover:text-bruno hover:border-argan transition-colors"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>

            {/* Body */}
            <div ref={bodyRef} className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-12">
              {loading && (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <Loader2 className="h-6 w-6 text-argan-deep animate-spin" strokeWidth={1.5} />
                  <p className="text-bruno-muted text-sm uppercase tracking-[0.2em]">
                    Caricamento articolo...
                  </p>
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <p className="font-display italic text-argan-deep text-xl">
                    Articolo non disponibile
                  </p>
                  <p className="mt-3 text-bruno-soft text-sm max-w-md mx-auto">
                    {error}
                  </p>
                </div>
              )}

              {article && !loading && !error && (
                <article
                  ref={articleRef}
                  className="max-w-none font-body text-bruno-soft"
                  dangerouslySetInnerHTML={{ __html: formatContent(article.content) }}
                />
              )}
            </div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
